import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { CheckCircle, Target, Award, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { getWebsiteImagesPublic } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AboutUsPage = () => {
  const [officeImage, setOfficeImage] = useState('https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/kuyif6c1_Who%20we%20Are.jpeg');

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const images = await getWebsiteImagesPublic();
        const aboutImg = images.find(img => img.section === 'about_office');
        if (aboutImg) {
          const imageUrl = (aboutImg.imageUrl.startsWith('/api/uploads') || aboutImg.imageUrl.startsWith('/uploads'))
            ? `${BACKEND_URL}${aboutImg.imageUrl}`
            : aboutImg.imageUrl;
          setOfficeImage(imageUrl);
        }
      } catch (error) {
        console.error('Failed to load about image:', error);
      }
    };
    fetchImages();
  }, []);

  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To empower real estate investors with fast, flexible financing solutions that fuel their success and growth."
    },
    {
      icon: CheckCircle,
      title: "Our Commitment",
      description: "We're committed to providing transparent, reliable lending services with zero hidden fees and genuine support."
    },
    {
      icon: Award,
      title: "Our Experience",
      description: "With over 15 years in the industry, we've helped hundreds of investors achieve their real estate goals."
    },
    {
      icon: Users,
      title: "Our Approach",
      description: "We treat every client as a partner, building lasting relationships based on trust and mutual success."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="About Us | Close Now Lending - Real Estate Investment Lender in Houston, TX"
        description="Learn about Close Now Lending, a Houston-based nationwide real estate investment lender with 15+ years of experience. We specialize in hard money, fix and flip, DSCR, and commercial loans with fast closings."
        keywords="Close Now Lending, about us, real estate lender Houston, hard money lender Texas, private money lender, real estate investment company, nationwide lending, fix and flip lender, DSCR lender, commercial real estate lender"
        url="/about"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-blue-600">Close Now Lending</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Your trusted partner in real estate investment financing
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center mb-16">
              <div className="lg:col-span-3">
                <video 
                  src="https://customer-assets.emergentagent.com/job_130b504f-d356-4ffc-9f88-b9ceecab1da1/artifacts/jx6gqjpi_AdobeStock_679988136.mov"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
              <div className="lg:col-span-2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Who We Are
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Close Now Lending was founded with a simple vision: to provide real estate investors with the fast, flexible financing they need to succeed in today's competitive market. Based in Houston, Texas, we've been serving investors across the nation for over 15 years.
                  </p>
                  <p>
                    What sets us apart is our unwavering commitment to our clients' success. We understand that time is money in real estate investing, which is why we've streamlined our processes to deliver approvals within 24 hours and closings in as few as 10 days.
                  </p>
                  <p>
                    Our team of experienced lending professionals brings decades of combined expertise in real estate finance. We don't just provide loans—we partner with you to find the best financing solution for your unique investment strategy.
                  </p>
                </div>
              </div>
            </div>

            {/* Values Grid */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                Our Values
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {values.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <Card key={index} className="border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
                      <CardContent className="p-8">
                        <div className="flex items-start mb-4">
                          <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                            <Icon className="h-7 w-7 text-blue-600" />
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 mt-2">
                            {value.title}
                          </h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                          {value.description}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Choose Close Now Lending?
            </h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Industry-Leading Speed</h3>
                  <p className="text-gray-700">Get pre-approved in 24 hours and close your deal in as few as 10 days. We understand that timing is everything in real estate.</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Transparent Pricing</h3>
                  <p className="text-gray-700">Competitive rates with no hidden fees or junk charges. Every cost is disclosed upfront so you can make informed decisions.</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Loan Products</h3>
                  <p className="text-gray-700">From fix-and-flip to long-term rentals, new construction to bridge loans, we have the right product for your investment strategy.</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Local Market Expertise</h3>
                  <p className="text-gray-700">Our team includes local experts who understand your market and can provide valuable insights for your investment decisions.</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Ongoing Support</h3>
                  <p className="text-gray-700">Our relationship doesn't end at closing. We're here to support you throughout your investment journey and for all your future projects.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Let's discuss how we can help finance your next investment property.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+19034762937"
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-lg"
              >
                Call Us: 903-476-2937
              </a>
              <a 
                href="/#loan-programs"
                className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors text-lg"
              >
                View Loan Programs
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUsPage;